import * as yup from 'yup';
import { Clock, Lock } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';

import { AddOrderStatus, PaymentMethodInput } from '__generated__/graphql';

import { Event, Registration } from '../helpers';
import { containsKeyPrefix, getServerErrors } from '../../../common/helpers';
import ConfirmLotteryResultMutation from './ConfirmLotteryResultMutation';
import PaymentMethodSelection from '../../../common/Payments/PaymentMethodSelection';
import UI from '../../../common/UI';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useOnPageCacheRestore from '../../../common/useOnPageCacheRestore';
import usePaymentMethods from '../../../common/usePaymentMethods';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';
import useValidation from '../../../common/useValidation';

const confirmLoteryResultSchema = yup.object({
  time_slot: yup.object({
    id: yup.string().required(),
  }).nullable(),
  payment_method: yup.object({
    payment_method: yup.string().ensure().required(),
  }).nullable(),
});

export interface ConfirmLotteryFormProps {
  registration: Registration;
  event: Event;
}

const ConfirmLotteryForm = ({ registration, event }: ConfirmLotteryFormProps) => {
  const { t } = useTranslation();
  const { formatCurrency, formatDate, parseDate } = useLocale();
  const formatTimeSlot = useTimeSlotFormatter();

  const { preregistration } = registration;
  const drawResult = preregistration.draw_result;

  const requireTimeSlot = !drawResult.time_slot && drawResult.ticket.upcoming_time_slots.length > 0;
  const requirePayment = drawResult.amount > 0;

  const form = useForm({
    values: {
      time_slot: requireTimeSlot ? { id: null } : null,
      payment_method: requirePayment ? {
        payment_method: null,
        issuer: null,
      } as PaymentMethodInput : null,
    },
  });

  // Used to disable the 'Pay' button while the browser is redirecting the user, to
  // prevent the user from creating another payment in the mean time.
  // Unfortunately we can't simply disable the button if confirmLotteryResultData is present,
  // because this state object is restored by Safari on pressing the browser back button.
  // In that case, the user should be able to resubmit the checkout.
  const [redirecting, setRedirecting] = useState(false);

  useOnPageCacheRestore(() => {
    // Enable Pay button
    setRedirecting(false);
  });

  const [confirmLotteryResult, { error, loading: submitting }] = useMutation(
    ConfirmLotteryResultMutation,
    {
      variables: {
        input: {
          registration_id: registration.id,
          time_slot_id: form.values.time_slot?.id,
          payment_method: form.values.payment_method,
          source_url: window.location.href,
        },
      },
      onCompleted: ({ confirmLotteryResult: { redirect_url, status } }) => {
        if (status === AddOrderStatus.SUCCESS) {
          if (redirect_url) {
            // Disable the 'Pay' button while redirecting.
            setRedirecting(true);
            window.location.href = redirect_url;
          }
        }
      },
      onError: () => {
        //
      },
    },
  );

  const loading = submitting || redirecting;

  const { valid, errors } = useValidation({
    schema: confirmLoteryResultSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const invalidPaymentMethod = containsKeyPrefix(errors, 'payment_method');

  const { allowedPaymentMethods } = usePaymentMethods({
    country: registration.country,
    enabledPaymentMethods: event.payment_methods,
    bankTransferAllowed: false,
  });

  return (
    <UI.FormGrid sc={{ padding: [3, 4] }}>
      <UI.Form onSubmit={() => confirmLotteryResult()}>
        <UI.FormGrid>
          <UI.ServerErrors error={error} />

          <UI.Div>
            <UI.Legend sc={{ fontWeight: 500, mb: 1 }}>
              <UI.Delimit>
                {t('confirm_ticket', { ticket: drawResult.ticket.title })}
                {!drawResult.promotion.standard
                  && drawResult.promotion.title}
                {drawResult.time_slot && formatTimeSlot(drawResult.time_slot)}
              </UI.Delimit>
            </UI.Legend>
            <Trans
              i18nKey={requirePayment ? 'confirm_lottery_description' : 'confirm_lottery_description_free'}
              values={{
                date: formatDate(parseDate(drawResult.end_date, { timezone: 'UTC' })),
              }}
            >
              <UI.Strong />
            </Trans>
          </UI.Div>

          {requireTimeSlot && (
            <UI.InputGroup sc={{ valid: !errors['time_slot.id'], touched: form.getTouched('time_slot') }}>
              <UI.InputLabel htmlFor="PreregistrationTimeSlot">
                {t('time_slot')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.InputWrapper>
                <UI.Icon sc={{ muted: true }}>
                  <Clock />
                </UI.Icon>
                <UI.Select
                  {...form.select('time_slot.id', drawResult.ticket.upcoming_time_slots.map(({ id }) => id))}
                  sc={{ hasValue: !!form.values.time_slot, pl: 5 }}
                  id="PreregistrationTimeSlot"
                >
                  {!form.values.time_slot?.id && (
                    <option value={-1} disabled>
                      {t('common:edit_registration_form.make_a_choice')}
                    </option>
                  )}
                  {drawResult.ticket.upcoming_time_slots.map((timeSlot, index) => (
                    <option
                      {...form.option(index)}
                      key={index}
                      disabled={timeSlot.id !== registration.time_slot?.id && timeSlot.is_sold_out}
                    >
                      {formatTimeSlot(timeSlot)}
                      {timeSlot.title && ` • ${timeSlot.title}`}
                      {timeSlot.id !== registration.time_slot?.id && timeSlot.is_sold_out
                        && ` • ${t('common:edit_registration_form.unavailable')}`}
                    </option>
                  ))}
                </UI.Select>
              </UI.InputWrapper>
              <UI.ErrorMessages attribute={t('time_slot')} errors={errors['time_slot.id']} />
            </UI.InputGroup>
          )}

          {requirePayment && (
            <>
              {drawResult.fee > 0 && (
                <>
                  <UI.InputGroup sc={{ mb: 0 }}>
                    <UI.InputLabel>
                      {t('ticket_price')}
                    </UI.InputLabel>
                    {formatCurrency(drawResult.amount)}
                  </UI.InputGroup>

                  <UI.InputGroup sc={{ mb: 0 }}>
                    <UI.InputLabel>
                      {t('platform_fee')}
                    </UI.InputLabel>
                    {formatCurrency(drawResult.fee)}
                  </UI.InputGroup>
                </>
              )}

              <UI.InputGroup sc={{ mb: 0, valid: !invalidPaymentMethod, touched: !!form.touched.payment_method }}>
                <UI.InputLabel>
                  {t('common:payments.payment_method')}
                  {' '}
                  <UI.RequiredMark sc={{ valid: !invalidPaymentMethod }} />
                </UI.InputLabel>
                <PaymentMethodSelection
                  value={form.values.payment_method}
                  onChange={(paymentMethod) => form.set('payment_method', paymentMethod)}
                  paymentMethods={allowedPaymentMethods}
                />
              </UI.InputGroup>
            </>
          )}

          <UI.Button
            type="submit"
            sc={{ brand: 'secondary', loading }}
            disabled={loading || !valid}
          >
            <UI.Icon>
              <Lock />
            </UI.Icon>
            {' '}
            {requirePayment
              ? t('pay_amount', {
                amount: formatCurrency(drawResult.amount + drawResult.fee),
              })
              : t('confirm')}
          </UI.Button>
        </UI.FormGrid>
      </UI.Form>
    </UI.FormGrid>
  );
};

export default ConfirmLotteryForm;
